'use client';

import { trpc } from '@/lib/trpc';
import type { SponsorshipLevel } from '@impact/types';

const SPONSOR_EVENTS_CACHE_MS = 1000 * 60 * 5;

export function useSponsorEventsByLocation(
  countryCode: string | undefined,
  stateCode: string | undefined,
) {
  return trpc.publicSponsors.eventsByLocation.useQuery(
    {
      countryCode: countryCode!,
      stateCode: stateCode || undefined,
    },
    {
      enabled: !!countryCode,
      staleTime: SPONSOR_EVENTS_CACHE_MS,
      gcTime: SPONSOR_EVENTS_CACHE_MS * 3,
    },
  );
}

interface SubmitSponsorApplicationInput {
  businessName: string;
  businessWebsite?: string;
  contribution?: string;
  contactFirstName: string;
  contactLastName: string;
  contactEmail: string;
  contactPhone?: string;
  logoAssetId?: string | null;
  selectedEvents: Array<{
    eventId: string;
    level: SponsorshipLevel;
  }>;
}

interface PrepareSponsorLogoUploadInput {
  filename: string;
  mimeType: string;
  sizeBytes: number;
}

interface CompleteSponsorLogoUploadInput {
  assetId: string;
}

export function useApprovedSponsorsForEvent(eventId: string | undefined) {
  return trpc.publicSponsors.approvedSponsorsForEvent.useQuery(
    { eventId: eventId! },
    {
      enabled: !!eventId,
      staleTime: SPONSOR_EVENTS_CACHE_MS,
      gcTime: SPONSOR_EVENTS_CACHE_MS * 3,
    },
  );
}

export function useStripePublicKey() {
  return trpc.publicSponsors.stripePublicKey.useQuery(undefined, {
    staleTime: SPONSOR_EVENTS_CACHE_MS,
    gcTime: SPONSOR_EVENTS_CACHE_MS * 3,
  });
}

export function useSubmitSponsorApplication() {
  return trpc.publicSponsors.submitApplication.useMutation();
}

export function usePrepareSponsorLogoUpload() {
  return trpc.publicSponsors.prepareLogoUpload.useMutation();
}

export function useCompleteSponsorLogoUpload() {
  return trpc.publicSponsors.completeLogoUpload.useMutation();
}

export type {
  SubmitSponsorApplicationInput,
  PrepareSponsorLogoUploadInput,
  CompleteSponsorLogoUploadInput,
};
